import { format } from 'date-fns';

import TipoDocumentoEnum from '../../Enum/TipoDocumentoEnum';
import StatusMotoristaEnum from '../../pages/Demanda/Enum/StatusMotoristaEnum';
import ListaDocumentosActionTypes from './actions';

/**
 * @typedef {Object} Documento
 * @property {number} id
 * @property {string} tipoDocumentoDescricao
 * @property {string} tipoDocumento
 * @property {string} dataCriacao
 * @property {string} link - link para dawnload usando a apiTmov
 * @property {boolean} isMaxicon - se documento é da maxys
 * @property {number} IdUsuario
 * @property {number} IdUsuarioShipper
 */

/**
 * @typedef {Object} ListaDocumentos
 * @property {string} tipoDocumentoDescricao
 * @property {string} tipoDocumento
 * @property {boolean} anexarEnable - pode fazer upload de arquivos
 * @property {boolean} editing - se o documento esta sendo editado em tela
 * @property {number} numAnexos
 * @property {Documento[]} documentos
 */

/**
 * @typedef {ListaDocumentos[]} INITIAL_LISTA_DOCUMENTOS
 */
const DEFAULT_NUM_ANEXOS = 1;

const DOCUMENTOS_INICIAL = [
  {
    tipoDocumentoDescricao: 'Ordem de Carregamento',
    tipoDocumento: TipoDocumentoEnum.OrdemDeCarregamento,
    anexarEnable: false,
    loading: false,
    editing: false,
    numAnexo: DEFAULT_NUM_ANEXOS,
    documentos: [],
  },
  {
    tipoDocumentoDescricao: 'NF-e',
    tipoDocumento: TipoDocumentoEnum.NFe,
    anexarEnable: false,
    loading: false,
    editing: false,
    numAnexo: 2,
    documentos: [],
  },
  {
    tipoDocumentoDescricao: 'Carta de correção',
    tipoDocumento: TipoDocumentoEnum.CartaDeCorrecao,
    anexarEnable: false,
    loading: false,
    editing: false,
    numAnexo: DEFAULT_NUM_ANEXOS,
    documentos: [],
  },
  {
    tipoDocumentoDescricao: 'Ticket de Pesagem',
    tipoDocumento: TipoDocumentoEnum.TicketDePesagem,
    anexarEnable: true,
    loading: false,
    editing: false,
    numAnexo: DEFAULT_NUM_ANEXOS,
    documentos: [],
  },
  {
    tipoDocumentoDescricao: 'Checklist',
    tipoDocumento: TipoDocumentoEnum.Checklist,
    anexarEnable: true,
    loading: false,
    editing: false,
    numAnexo: DEFAULT_NUM_ANEXOS,
    documentos: [],
  },
  {
    tipoDocumentoDescricao: 'Laudo de Classificação',
    tipoDocumento: TipoDocumentoEnum.LoudoDeClassificacao,
    anexarEnable: true,
    loading: false,
    editing: false,
    numAnexo: DEFAULT_NUM_ANEXOS,
    documentos: [],
  },
  {
    tipoDocumentoDescricao: 'CT-e',
    tipoDocumento: TipoDocumentoEnum.CTe,
    anexarEnable: false,
    loading: false,
    editing: false,
    numAnexo: DEFAULT_NUM_ANEXOS,
    documentos: [],
  },
  {
    tipoDocumentoDescricao: 'NFS-e',
    tipoDocumento: TipoDocumentoEnum.NFSe,
    anexarEnable: false,
    loading: false,
    editing: false,
    numAnexo: DEFAULT_NUM_ANEXOS,
    documentos: [],
  },
  {
    tipoDocumentoDescricao: 'MDF-e',
    tipoDocumento: TipoDocumentoEnum.MDFe,
    anexarEnable: false,
    loading: false,
    editing: false,
    numAnexo: DEFAULT_NUM_ANEXOS,
    documentos: [],
  },
  {
    tipoDocumentoDescricao: 'CF-e',
    tipoDocumento: TipoDocumentoEnum.CFe,
    anexarEnable: false,
    loading: false,
    editing: false,
    numAnexo: DEFAULT_NUM_ANEXOS,
    documentos: [],
  },
  {
    tipoDocumentoDescricao: 'Contrato frete',
    tipoDocumento: TipoDocumentoEnum.ContratoDeTransporte,
    anexarEnable: false,
    loading: false,
    editing: false,
    numAnexo: DEFAULT_NUM_ANEXOS,
    documentos: [],
  },
  {
    tipoDocumentoDescricao: 'Termo de estadia',
    tipoDocumento: TipoDocumentoEnum.TermoDeEstadia,
    anexarEnable: false,
    loading: false,
    editing: false,
    numAnexo: DEFAULT_NUM_ANEXOS,
    documentos: [],
  },
  {
    tipoDocumentoDescricao: 'Carta frete',
    tipoDocumento: TipoDocumentoEnum.CartaFrete,
    anexarEnable: false,
    loading: false,
    editing: false,
    numAnexo: DEFAULT_NUM_ANEXOS,
    documentos: [],
  },
  {
    tipoDocumentoDescricao: 'Comprovante de descarga',
    tipoDocumento: TipoDocumentoEnum.ComprovanteDeDescarga,
    anexarEnable: false,
    loading: false,
    editing: false,
    numAnexo: DEFAULT_NUM_ANEXOS,
    documentos: [],
  },
];

const handleEnableAnexo = tipoDocumento => {
  const documentos = [TipoDocumentoEnum.NFe, TipoDocumentoEnum.CartaDeCorrecao];
  return documentos.includes(tipoDocumento);
};

const ListaDocumentosReducer = (state, action) => {
  const { payload } = action;

  switch (action.type) {
    case ListaDocumentosActionTypes.UPDATE:
      return DOCUMENTOS_INICIAL.map(docList => {
        const documentosDownloads = payload.documentosMotorista.filter(
          doc => doc.tipoDocumento === docList.tipoDocumento
        );

        if (handleEnableAnexo(docList.tipoDocumento)) {
          docList.anexarEnable =
            payload.statusViagem === StatusMotoristaEnum.EMBARCADO;
        }

        return {
          ...docList,
          ...(documentosDownloads.length && {
            documentos: documentosDownloads,
          }),
        };
      });
    case ListaDocumentosActionTypes.EDIT:
      return state.map(docList => {
        if (docList.tipoDocumento === payload) {
          docList.editing = !docList.editing;
        }
        return docList;
      });
    case ListaDocumentosActionTypes.LOADING:
      return state.map(docList => {
        if (docList.tipoDocumento === payload.tipoDocumento) {
          docList.loading = payload.loading;
        }
        return docList;
      });
    case ListaDocumentosActionTypes.REMOVE:
      return state.map(docList => {
        if (docList.tipoDocumento === payload.tipoDocumento) {
          const documentosRestante = docList.documentos.filter(
            doc => doc.id !== payload.id
          );
          docList.documentos = documentosRestante;
        }
        return docList;
      });
    case ListaDocumentosActionTypes.ADD:
      return state.map(docList => {
        if (docList.tipoDocumento === payload.tipoDocumento) {
          const documentoNovo = {
            id: payload.id,
            tipoDocumento: docList.tipoDocumento,
            tipoDocumentoDescricao: docList.tipoDocumentoDescricao,
            dataCriacao: format(new Date(), "dd/MM/yyyy 'às' HH:mm"),
            isMaxicon: false,
            link: `/documento/download/${payload.id}`,
            idUsuarioShipper: payload.idUsuarioShipper,
            idUsuario: null,
          };

          docList.documentos = [...docList.documentos, documentoNovo];
        }
        return docList;
      });
    default:
      return state;
  }
};

export { DOCUMENTOS_INICIAL, ListaDocumentosReducer };
